/**
 * AssetGroup Store
 *
 * Holds the asset group currently open in the Asset Group Editor: its root details, the asset rows with their
 * column children, and the editor's loading and unsaved-changes flags. Persisted at the WORKSHEET level.
 *
 * The row/column bookkeeping lives in the pure helpers in `assetGroupAssets.utilities.ts`.
 */

import { useEffect } from 'react';
import { createSelector } from 'reselect';
import {
  addAsset,
  addAttribute,
  addCalculationChildToAsset,
  addChildToAsset,
  addEmptyAsset,
  getColumnsHelper,
  removeAttribute,
  removeChildFromAsset,
  restoreChild,
  updateAssetName,
  updateChildParameter,
  updateColumnName,
} from '@/assetGroupEditor/assetGroupAssets.utilities';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { AssetGroup, AssetGroupAsset, AssetGroupChild } from '@/assetGroupEditor/assetGroup.types';
import type { AssetGroupCalculationChild, NewAssetGroupAttribute } from '@/assetGroupEditor/assetGroupAssets.utilities';

export interface AssetGroupState {
  /** ID of the asset group being edited, or `''` for a group that has not been saved yet. */
  id: string;
  /** Name of the asset group. */
  name: string;
  /** Description of the asset group. */
  description: string;
  /** The rows of the asset group; each asset's children are its columns. */
  assets: AssetGroupAsset[];
  /** Errors from the last save. Kept for persisted-state compatibility; nothing sets it. */
  errors: string[];
  /** True while an asset group, or an asset being added to it, is loading. */
  isLoading: boolean;
  /** True when the editor has changes that have not been saved. */
  hasUnsavedChanges: boolean;
  /** True when the current name is invalid (blank). Not persisted. */
  nameError: boolean;
  /** ID of the user that created the asset group. */
  addedBy: string;
}

/** Everything except `nameError` is persisted */
export type AssetGroupDehydratedState = Omit<AssetGroupState, 'nameError'>;

const initialState: AssetGroupState = Object.freeze({
  id: '',
  name: '',
  description: '',
  assets: [],
  errors: [],
  isLoading: false,
  hasUnsavedChanges: false,
  nameError: false,
  addedBy: '',
});

export const dehydrateAssetGroupState = ({ nameError: _nameError, ...rest }: AssetGroupState) => rest;

export interface AssetGroupActions {
  /** Merges a loaded asset group into the store. */
  setAssetGroup: (assetGroup: AssetGroup) => void;
  /** Sets the asset group name. Does not validate it; see `setNameError`. */
  setName: (name: string) => void;
  /** Flags whether the current name is invalid (blank). */
  setNameError: (nameError: boolean) => void;
  /** Sets the asset group description. */
  setDescription: (description: string) => void;
  /** Sets whether an asset group, or an asset being added to it, is loading. */
  setIsLoading: (isLoading: boolean) => void;
  /** Sets whether the editor has changes that have not been saved. */
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  /** Adds an asset, or merges its children into the matching asset, keeping asset names unique. */
  addAsset: (asset: AssetGroupAsset) => void;
  /** Adds a manually created row with a placeholder for every empty column and every calculation column. */
  addEmptyAsset: (asset: AssetGroupAsset, currentUserId: string) => void;
  /**
   * Adds or replaces an item (non-calculation) child on the matching asset. The first item ever assigned to a child is
   * recorded as its `originalMapping`.
   */
  addChildToAsset: (asset: AssetGroupAsset, child: AssetGroupChild) => void;
  /**
   * Adds a calculation child to the asset with the same name, replacing an existing child of the same name. Only the
   * parameters the formula references are kept.
   */
  addCalculationChildToAsset: (asset: AssetGroupAsset, child: AssetGroupCalculationChild) => void;
  /** Clears the item of the column-based parameter that pointed at a removed column. */
  updateChildParameter: (asset: AssetGroupAsset, child: AssetGroupChild, parameterItemName: string) => void;
  /** Points the child named `name` back at `newItemId`. */
  restoreChild: (asset: AssetGroupAsset, name: string, newItemId: string) => void;
  /** Removes the child named `child.name` from the matching asset, leaving the column on the other assets. */
  removeChildFromAsset: (asset: AssetGroupAsset, child: AssetGroupChild) => void;
  /** Adds a column to every asset. */
  addAttribute: (attribute: NewAssetGroupAttribute) => void;
  /** Removes the column named `name` from every asset. */
  removeAttribute: (name: string) => void;
  /** Renames a column and updates the calculations that reference it. */
  updateColumnName: (originalName: string, newName: string) => void;
  /** Renames the asset that has the same name and path as `original`. Does not check that the name is unique. */
  updateAssetName: (original: AssetGroupAsset, newName: string) => void;
  /** Removes every asset named `name`. */
  removeAsset: (name: string) => void;
  /** Restores the initial state, closing the asset group. */
  reset: () => void;
}

const assetGroupActionsFactory: ActionsFactory<AssetGroupState, AssetGroupActions> = (set, get, resetState) => {
  const setAssets = (assets: AssetGroupAsset[]) =>
    set((draft) => {
      draft.assets = assets;
    });

  return {
    setAssetGroup: (assetGroup) =>
      set((draft) => {
        Object.assign(draft, assetGroup);
      }),

    setName: (name) =>
      set((draft) => {
        draft.name = name;
      }),

    setNameError: (nameError) =>
      set((draft) => {
        draft.nameError = nameError;
      }),

    setDescription: (description) =>
      set((draft) => {
        draft.description = description;
      }),

    setIsLoading: (isLoading) =>
      set((draft) => {
        draft.isLoading = isLoading;
      }),

    setHasUnsavedChanges: (hasUnsavedChanges) =>
      set((draft) => {
        draft.hasUnsavedChanges = hasUnsavedChanges;
      }),

    addAsset: (asset) => setAssets(addAsset(get().assets, asset)),

    addEmptyAsset: (asset, currentUserId) => setAssets(addEmptyAsset(get().assets, asset, currentUserId)),

    addChildToAsset: (asset, child) => setAssets(addChildToAsset(get().assets, asset, child)),

    addCalculationChildToAsset: (asset, child) => setAssets(addCalculationChildToAsset(get().assets, asset, child)),

    updateChildParameter: (asset, child, parameterItemName) =>
      setAssets(updateChildParameter(get().assets, asset, child, parameterItemName)),

    restoreChild: (asset, name, newItemId) => setAssets(restoreChild(get().assets, asset, name, newItemId)),

    removeChildFromAsset: (asset, child) => setAssets(removeChildFromAsset(get().assets, asset, child)),

    addAttribute: (attribute) => setAssets(addAttribute(get().assets, attribute)),

    removeAttribute: (name) => setAssets(removeAttribute(get().assets, name)),

    updateColumnName: (originalName, newName) => setAssets(updateColumnName(get().assets, originalName, newName)),

    updateAssetName: (original, newName) => setAssets(updateAssetName(get().assets, original, newName)),

    removeAsset: (name) =>
      set((draft) => {
        draft.assets = draft.assets.filter((asset) => asset.name !== name);
      }),

    reset: resetState,
  };
};

const STORE_NAME = 'AssetGroup';

const [AssetGroupStoreProvider, useAssetGroupStoreInstance, useAssetGroupSelector] = createStore(
  STORE_NAME,
  initialState,
  assetGroupActionsFactory,
);

export { AssetGroupStoreProvider };

export const useAssetGroupId = () => useAssetGroupSelector((state) => state.id);
export const useAssetGroupName = () => useAssetGroupSelector((state) => state.name);
export const useAssetGroupNameError = () => useAssetGroupSelector((state) => state.nameError);
export const useAssetGroupDescription = () => useAssetGroupSelector((state) => state.description);
export const useAssetGroupAssets = () => useAssetGroupSelector((state) => state.assets);
export const useAssetGroupIsLoading = () => useAssetGroupSelector((state) => state.isLoading);
export const useAssetGroupHasUnsavedChanges = () => useAssetGroupSelector((state) => state.hasUnsavedChanges);

/** True when `itemId` is the asset group currently loaded in the store. */
export const useIsCurrentAssetGroup = (itemId: string | undefined) =>
  useAssetGroupSelector((state) => state.id === itemId);

/** The distinct columns across all assets, sorted case-insensitively by name. */
const selectColumns = createSelector((state: AssetGroupState) => state.assets, getColumnsHelper);
export const useAssetGroupColumns = () => useAssetGroupSelector(selectColumns);

export const useAssetGroupActions = () => useAssetGroupStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateAssetGroupStore(initialStateOverrides?: Partial<AssetGroupState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    assetGroupActionsFactory,
    { storeName: STORE_NAME },
  );
}

export type AssetGroupStore = ReturnType<typeof legacyCreateAssetGroupStore>;

/** Registers the AssetGroup store with the persistence pipeline so an open asset group survives worksheet loads. */
export const usePersistAssetGroupStore = () => {
  const register = useRegisterPersistedStore();
  const store = useAssetGroupStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqAssetGroupStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateAssetGroupState,
    });
  }, [register, store]);
};
